import { calculateProductTaxValue, calculateTax } from '@e3labs/isa-functions-dictionary';
import {
  buildDataInterface,
  getBaseSTProps,
  getTaxProps,
  getTaxResultInterface,
  taxByPriceTableProps,
  taxSTFonteProps,
  taxSTNormalProps,
  taxValuesInterface
} from './tributacao-interface';
import { toDecimals } from '~/utils/format';

export const getTax = ({
  client = null,
  configuration,
  paymentPlan,
  product,
  soldPrice,
  soldQuantity = 1,
  shippingType = '',
  tenantID = null,
  tributacao = null,
  vendorDiscountPercentage = 0
}: getTaxProps): getTaxResultInterface => {
  let tax: taxValuesInterface = {
    valueFCP: 0,
    valueIPI: 0,
    valueST: 0,
    valueRedCofins: 0,
    valueRedICMS: 0,
    valueRedPIS: 0,
    valueREP: 0,
    totalReduction: 0
  };

  const precision = configuration?.decimals ?? 2;

  const isIPIExempt = client?.isIPIExempt;
  const isSuframaExempt = client?.isSuframaExempt;

  const valueIPI = isIPIExempt ? 0 : toDecimals(soldPrice * ((product.percIpi ?? 0) / 100), precision);
  tax.valueIPI = valueIPI;

  const { messageReduction, valueRedICMS, valueRedPIS, valueRedCofins, totalReduction } = reductionCalculation({
    isSuframaExempt,
    soldPrice,
    tributacao,
    precision
  });
  tax.valueRedICMS = valueRedICMS;
  tax.valueRedPIS = valueRedPIS;
  tax.valueRedCofins = valueRedCofins;
  tax.totalReduction = totalReduction;

  const clientDiscount = client?.discountPercentage ?? 0;
  const paymentPlanDiscount = paymentPlan?.financialPercentageRate ?? 0;
  const freightDiscount =
    configuration.useDiscountFreightCollect && shippingType === 'free'
      ? product?.price.freightDiscountPercentage ?? 0
      : 0;

  const productDiscountPercentage = clientDiscount + paymentPlanDiscount + freightDiscount;

  if (!tributacao || !client?.applyTax) {
    return buildData({
      precision,
      soldPrice,
      soldQuantity,
      tax,
      tributacao
    });

    //Cálculo de Emefarma
  } else if (['5ecfd75991d20cef0af1b94d', '5f21c85d4be5b0220d9b0cbd'].includes(tenantID)) {
    tax = {
      ...tax,
      ...taxEmefarma({
        product,
        soldPrice,
        tributacao,
        valueIPI
      })
    };
  } else if (['67f91b941debdfd5471d026f', '67f91b941debdfd5471d026f'].includes(tenantID)) {
    tax = {
      ...tax,
      ...taxNutriport({
        precision,
        product,
        soldPrice,
        tributacao,
        valueIPI
      })
    };
  } else if (configuration.useTaxByPriceTable) {
    tax = {
      ...tax,
      ...taxByPriceTable({
        priceNumber: paymentPlan && paymentPlan?.priceNumber !== 1 ? String(paymentPlan?.priceNumber) : '',
        product,
        productDiscountPercentage,
        vendorDiscountPercentage
      })
    };
  } else if (client.applyTaxST) {
    tax = {
      ...tax,
      ...taxSTNormal({
        comparesMinimumPriceWithST: configuration?.comparesMinimumPriceWithST,
        product,
        soldPrice,
        tributacao,
        valueIPI
      })
    };
  } else if (client.applyTax2) {
    tax = {
      ...tax,
      ...taxSTFonte({
        product,
        soldPrice,
        tributacao,
        valueIPI
      })
    };
  } else {
    tax = {
      ...tax,
      ...taxSTNormal({
        comparesMinimumPriceWithST: configuration?.comparesMinimumPriceWithST,
        product,
        soldPrice,
        tributacao,
        valueIPI
      })
    };
  }

  return buildData({
    precision,
    soldPrice,
    soldQuantity,
    tax,
    tributacao
  });
};

const getBaseST = ({ soldPrice, price, tributacao }: getBaseSTProps): number => {
  if (tributacao.usapmcbasest) {
    return price.maxConsumerPrice;
  } else if (tributacao.usaptabelabasest) {
    return price.factoryPrice;
  } else if (tributacao.usapmpfbasest) {
    return price.maxStatePrice;
  } else {
    return soldPrice;
  }
};

const taxEmefarma = ({ product, soldPrice, tributacao, valueIPI }): taxValuesInterface => {
  const valorBaseST =
    getBaseST({
      price: product.price,
      soldPrice,
      tributacao
    }) + valueIPI;

  const valorBaseICMS = soldPrice * (1 - tributacao?.repasse / 100) * (1 - tributacao?.redbaseicmsnor / 100);

  const valorICMS = valorBaseICMS * ((tributacao?.aliqicms + tributacao?.aliqfcpicms) / 100);

  const valorBaseICMSRET = valorBaseST * (1 + tributacao?.baseicmsret / 100) * (1 - tributacao?.redbaseicmsret / 100);

  const valorIcmsRetido = valorBaseICMSRET * ((tributacao?.icmsretido + tributacao?.aliqfcpicms) / 100);

  const valorICMSST = valorIcmsRetido - valorICMS;

  const fecoepNormal = soldPrice * ((tributacao.percfecoep ?? 0) / 100);

  const valueFCP = valorBaseST * ((tributacao.percfecoep ?? 0) / 100) - fecoepNormal;

  return {
    valueFCP,
    valueIPI,
    valueST: valorICMSST
  };
};

const taxNutriport = ({ precision, product, soldPrice, tributacao, valueIPI }) => {
  const unitaryValueST =
    getBaseST({
      price: product.price,
      soldPrice,
      tributacao
    }) + valueIPI;

  const valueREP = toDecimals(unitaryValueST * ((tributacao?.repasse ?? 0) / 100), precision);

  const baseCalculo = unitaryValueST + valueREP;

  const margemValorAgregado = baseCalculo * ((tributacao?.ivafonte ?? 0) / 100);

  const valorBaseST = baseCalculo + margemValorAgregado;

  const creditICMS = baseCalculo * ((tributacao?.aliqicms1fonte ?? 0) / 100);

  const debitoST = valorBaseST * ((tributacao?.aliqicms2fonte ?? 0) / 100);

  const valueST = toDecimals(debitoST - creditICMS, precision);

  const valueFCP = toDecimals(valorBaseST * ((tributacao.percfecoep ?? 0) / 100), precision);

  return {
    valueFCP,
    valueIPI,
    valueST,
    valueREP
  };
};

const taxByPriceTable = ({
  priceNumber = '',
  product,
  productDiscountPercentage = 0,
  vendorDiscountPercentage = 0
}: taxByPriceTableProps): taxValuesInterface => {
  const ST = calculateTax(product.price.taxValue, product.price.price, product.price[`price${priceNumber}`]);
  const FCP = calculateTax(product.price.taxFCPValue, product.price.price, product.price[`price${priceNumber}`]);
  const IPI = calculateTax(product.price.taxIPIValue, product.price.price, product.price[`price${priceNumber}`]);

  const valueST =
    calculateProductTaxValue(ST, productDiscountPercentage, vendorDiscountPercentage) *
    (product?.pack?.packingQuantity ?? 1);

  const valueFCP =
    calculateProductTaxValue(FCP, productDiscountPercentage, vendorDiscountPercentage) *
    (product?.pack?.packingQuantity ?? 1);

  const valueIPI =
    calculateProductTaxValue(IPI, productDiscountPercentage, vendorDiscountPercentage) *
    (product?.pack?.packingQuantity ?? 1);

  return {
    valueFCP,
    valueIPI,
    valueST
  };
};

const taxSTNormal = ({
  comparesMinimumPriceWithST,
  product,
  tributacao,
  soldPrice,
  valueIPI
}: taxSTNormalProps): taxValuesInterface => {
  const baseICMS = soldPrice * (1 - (tributacao?.percbasered ?? 0) / 100);

  const creditICMS = baseICMS * ((tributacao?.aliqicms1 ?? 0) / 100);

  const unitaryValueST =
    getBaseST({
      soldPrice,
      price: product.price,
      tributacao
    }) + valueIPI;

  let minPrice = product.prices.find(
    price =>
      price.minimumMarketPrice &&
      price.productExternalCode === product.externalCode &&
      price.priceTableCode === tributacao.minimumMarketPriceExternalCode
  )?.price;

  let valorBaseST = (unitaryValueST + (unitaryValueST * tributacao.iva) / 100) * (1 - tributacao.percbaseredst / 100);

  if (comparesMinimumPriceWithST && valorBaseST < (minPrice ?? 0)) {
    valorBaseST = minPrice;
  }

  /* const fecoepNormal = soldPrice * ((tributacao.percfecoep ?? 0) / 100);

  const valueFCP = valorBaseST * ((tributacao.percfecoep ?? 0) / 100) - fecoepNormal; */

  const valueFCP = valorBaseST * ((tributacao.percfecoep ?? 0) / 100);

  const debitoST = valorBaseST * (tributacao.aliqicms2 / 100);

  const valueST = debitoST - creditICMS;

  return {
    valueFCP,
    valueIPI,
    valueST
  };
};

const taxSTFonte = ({ product, soldPrice, tributacao, valueIPI }: taxSTFonteProps): taxValuesInterface => {
  const baseICMS = soldPrice * (1 - (tributacao.percbasered ?? 0) / 100);

  const creditICMS = baseICMS * ((tributacao.aliqicms1fonte ?? 0) / 100);

  const unitaryValueST =
    getBaseST({
      soldPrice,
      price: product.price,
      tributacao
    }) + valueIPI;

  const valorBaseST =
    unitaryValueST +
    (unitaryValueST - (unitaryValueST * (tributacao.percbaseredstfonte ?? 0)) / 100) *
      ((tributacao.ivafonte ?? 0) / 100);

  const fecoepNormal = soldPrice * ((tributacao.percfecoep ?? 0) / 100);

  const valueFCP = valorBaseST * ((tributacao.percfecoep ?? 0) / 100) - fecoepNormal;

  const debitoST = valorBaseST * (tributacao.aliqicms2fonte / 100);

  const valueST = debitoST - creditICMS;

  return {
    valueFCP,
    valueIPI,
    valueST
  };
};

const buildData = ({
  precision = 2,
  soldPrice,
  soldQuantity = 1,
  tax,
  tributacao
}: buildDataInterface): getTaxResultInterface => {
  const vlFCP = toDecimals(tax?.valueFCP || 0, precision);
  const vlIPI = toDecimals(tax?.valueIPI || 0, precision);
  const vlST = toDecimals(tax?.valueST || 0, precision);
  const vlRPS = toDecimals(tax?.valueREP || 0, precision);
  const vlRICMS = toDecimals(tax?.valueRedICMS || 0, precision);
  const vlRPIS = toDecimals(tax?.valueRedPIS || 0, precision);
  const vlRCofins = toDecimals(tax?.valueRedCofins || 0, precision);

  const vlTributos = vlFCP + vlIPI + vlST + vlRPS;
  const vlReduction = tax?.totalReduction;

  const totalPrice = soldPrice + vlTributos + vlReduction;

  return {
    data: tributacao ?? {},
    values: {
      vlFCP,
      vlIPI,
      vlST,
      vlRPS,
      vlRICMS,
      vlRPIS,
      vlRCofins,
      vlComST: totalPrice,
      unitaryValue: soldPrice,
      vlTributosUnitary: vlTributos ?? 0,
      vlReductionUnitary: vlReduction ?? 0
    },
    vlTributos: vlTributos * soldQuantity,
    vlReduction: vlReduction * soldQuantity,
    taxPercentage: vlTributos / (totalPrice + vlTributos)
  };
};

const reductionCalculation = ({ isSuframaExempt, soldPrice, tributacao, precision = 2 }) => {
  let messageReduction = '';
  if (!isSuframaExempt) {
    messageReduction += 'valueRedICMS = 0\n\n';
    messageReduction += 'valueRedPIS = 0\n\n';
    messageReduction += 'valueRedCofins = 0\n\n';
    messageReduction += 'totalReduction = 0\n\n';

    return {
      valueRedICMS: 0,
      valueRedPIS: 0,
      valueRedCofins: 0,
      totalReduction: 0,
      messageReduction
    };
  }

  const valueRedICMS = isSuframaExempt
    ? toDecimals(soldPrice * ((tributacao?.perdescsuframa ?? 0) / 100), precision) * -1
    : 0;
  const valueRedPIS = isSuframaExempt
    ? toDecimals(soldPrice * ((tributacao?.percdescpis ?? 0) / 100), precision) * -1
    : 0;
  const valueRedCofins = isSuframaExempt
    ? toDecimals(soldPrice * ((tributacao?.percdesccofins ?? 0) / 100), precision) * -1
    : 0;

  const totalReduction = valueRedICMS + valueRedPIS + valueRedCofins;

  messageReduction += 'valueRedICMS = soldPrice * ((tributacao?.perdescsuframa ?? 0) / 100) * -1\n';
  messageReduction += `valueRedICMS = ${soldPrice} * ((${tributacao?.perdescsuframa} ?? 0) / 100) * -1\n`;
  messageReduction += `valueRedICMS = ${soldPrice} * ((${tributacao?.perdescsuframa ?? 0}) / 100) * -1\n`;
  messageReduction += `valueRedICMS = ${soldPrice} * ${(tributacao?.perdescsuframa ?? 0) / 100} * -1\n`;
  messageReduction += `valueRedICMS = ${valueRedICMS}\n\n`;

  messageReduction += 'valueRedPIS = soldPrice * ((tributacao?.percdescpis ?? 0) / 100) * -1\n';
  messageReduction += `valueRedPIS = ${soldPrice} * ((${tributacao?.percdescpis} ?? 0) / 100) * -1\n`;
  messageReduction += `valueRedPIS = ${soldPrice} * ((${tributacao?.percdescpis ?? 0}) / 100) * -1\n`;
  messageReduction += `valueRedPIS = ${soldPrice} * ${(tributacao?.percdescpis ?? 0) / 100} * -1\n`;
  messageReduction += `valueRedPIS = ${valueRedPIS}\n\n`;

  messageReduction += 'valueRedCofins = soldPrice * ((tributacao?.percdesccofins ?? 0) / 100) * -1\n';
  messageReduction += `valueRedCofins = ${soldPrice} * ((${tributacao?.percdesccofins} ?? 0) / 100) * -1\n`;
  messageReduction += `valueRedCofins = ${soldPrice} * ((${tributacao?.percdesccofins ?? 0}) / 100) * -1\n`;
  messageReduction += `valueRedCofins = ${soldPrice} * ${(tributacao?.percdesccofins ?? 0) / 100} * -1\n`;
  messageReduction += `valueRedCofins = ${valueRedCofins}\n\n`;

  messageReduction += `totalReduction = valueRedICMS + valueRedPIS + valueRedCofins\n`;
  messageReduction += `totalReduction = ${valueRedICMS} + ${valueRedPIS} + ${valueRedCofins}\n`;
  messageReduction += `totalReduction = ${totalReduction}\n\n`;

  return {
    valueRedICMS,
    valueRedPIS,
    valueRedCofins,
    totalReduction,
    messageReduction
  };
};
